import React, { useEffect, useMemo, useState } from "react"
import { Alert, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap"
import { buildOperations, changedAudiences, describeChanges } from "./mustReadDraft"
import { publishMustRead, previewMustReadRecipients } from "helpers/mustReadApi"

const KIND_LABEL = { create: "New", edit: "Edited", retire: "Retiring", reorder: "Reordered" }
const newKey = () =>
  window.crypto?.randomUUID?.() ||
  "xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16))

const MustReadPublishModal = ({ isOpen, state, dispatch, onCancel, onPublished }) => {
  const [notifyEmail, setNotifyEmail] = useState(false)
  const [recipients, setRecipients] = useState(null)
  const [recipientsError, setRecipientsError] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [idempotencyKey, setIdempotencyKey] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    setIdempotencyKey(newKey())
    setNotifyEmail(false)
    setRecipients(null)
    setRecipientsError(false)
    setError(null)
  }, [isOpen])

  const changes = useMemo(() => describeChanges(state), [state])
  const rowsByKey = useMemo(() => new Map(state.rows.map((r) => [r.tempId || r.id, r])), [state.rows])
  const reReadRows = changes.filter((c) => c.kind === "edit" && c.contentChanged).map((c) => rowsByKey.get(c.key))
  const allReRead = reReadRows.length > 0 && reReadRows.every((r) => r.requiresReview)

  useEffect(() => {
    if (!isOpen || !notifyEmail) return
    setRecipientsError(false)
    const audiences = changedAudiences(state)
    if (!audiences.length) {
      setRecipients(0)
      return
    }
    previewMustReadRecipients(audiences)
      .then((d) => setRecipients(d.count))
      .catch(() => {
        setRecipients(null)
        setRecipientsError(true)
      })
  }, [isOpen, notifyEmail, state])

  const publish = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const result = await publishMustRead({ idempotencyKey, notifyEmail, operations: buildOperations(state) })
      onPublished(result)
    } catch (e) {
      const data = e?.response?.data || {}
      const failing = Number.isInteger(data.index) && changes[data.index] ? ` (${changes[data.index].title})` : ""
      setError(`${data.message || "Publish failed"}${failing}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={submitting ? undefined : onCancel} size="lg">
      <ModalHeader toggle={submitting ? undefined : onCancel}>Review & publish</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}

        {reReadRows.length > 1 && (
          <div className="form-check form-switch mb-3">
            <Input
              id="mr-reread-all"
              type="checkbox"
              className="form-check-input"
              checked={allReRead}
              onChange={(e) => dispatch({ type: "setAllRequiresReview", value: e.target.checked })}
            />
            <Label className="form-check-label" for="mr-reread-all">Set all edited docs to require re-read</Label>
          </div>
        )}

        <ul className="list-unstyled mb-4">
          {changes.map((c) => {
            const row = rowsByKey.get(c.key)
            const showSwitch = c.kind === "edit" && c.contentChanged
            return (
              <li key={c.key} className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom">
                <div>
                  <span className="mr-tag me-2">{KIND_LABEL[c.kind]}</span>
                  <span className="fw-semibold">{c.title}</span>
                  {c.kind === "create" && <div className="text-muted small">Unread for its audience</div>}
                  {c.kind === "edit" && !c.contentChanged && <div className="text-muted small">Details only; progress unaffected</div>}
                  {showSwitch && row?.changeNote && <div className="text-muted small">What changed: {row.changeNote}</div>}
                </div>
                {showSwitch && (
                  <div className="form-check form-switch m-0">
                    <Input
                      id={`mr-reread-${c.key}`}
                      type="checkbox"
                      className="form-check-input"
                      checked={!!row?.requiresReview}
                      onChange={(e) => dispatch({ type: "setRequiresReview", key: c.key, value: e.target.checked })}
                      aria-label={`Requires re-read: ${c.title}`}
                    />
                    <Label className="form-check-label small" for={`mr-reread-${c.key}`}>Requires re-read</Label>
                  </div>
                )}
              </li>
            )
          })}
        </ul>

        <div className="form-check form-switch">
          <Input
            id="mr-notify"
            type="checkbox"
            className="form-check-input"
            checked={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.checked)}
          />
          <Label className="form-check-label" for="mr-notify">Send email notification</Label>
        </div>
        {notifyEmail && (
          <div className="text-muted small mt-1">
            {recipientsError
              ? "Couldn’t count recipients. Emails still go to everyone in the selected audiences."
              : recipients === null
                ? "Counting recipients…"
                : `Will email ${recipients} users`}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onCancel} disabled={submitting}>Back to editing</Button>
        <Button className="mr-btn-primary" onClick={publish} disabled={submitting || changes.length === 0}>
          {submitting ? <Spinner size="sm" /> : "Publish changes"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default MustReadPublishModal
